import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import './SignupLinksWidget.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const SignupLinksWidget = ({ token }) => {
  const [signupLinks, setSignupLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAllModal, setShowAllModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [detailsModalData, setDetailsModalData] = useState(null);
  const [qrModalData, setQRModalData] = useState(null);
  const [message, setMessage] = useState('');
  const qrCodeRef = useRef(null);

  // Service groups for filtering
  const [serviceGroups, setServiceGroups] = useState([]);

  // Filters (for modal view)
  const [filters, setFilters] = useState({
    serviceGroupId: '',
    status: ['active'], // Default to active only
    searchTerm: '',
    createdFrom: '',
    createdTo: ''
  });

  // Pagination (for modal view)
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });

  useEffect(() => {
    fetchSignupLinks(false); // Widget view: last 5
    fetchServiceGroups();
  }, []);

  const fetchServiceGroups = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/service-groups`, {
        headers: { 'x-auth-token': token }
      });
      setServiceGroups(response.data.serviceGroups || []);
    } catch (error) {
      console.error('Error fetching service groups:', error);
    }
  };

  const fetchSignupLinks = async (isFullView = false) => {
    try {
      setLoading(true);

      const params = {
        type: 'general_link', // ONLY general links
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };

      if (isFullView) {
        // Full view with filters and pagination
        params.page = pagination.page;
        params.limit = pagination.limit;
        if (filters.serviceGroupId) params.serviceGroupId = filters.serviceGroupId;
        if (filters.status.length > 0) params.status = filters.status.join(',');
        if (filters.searchTerm) params.searchTerm = filters.searchTerm;
        if (filters.createdFrom) params.createdFrom = filters.createdFrom;
        if (filters.createdTo) params.createdTo = filters.createdTo;
      } else {
        // Widget view: last 5 active links
        params.page = 1;
        params.limit = 5;
        params.status = 'active';
      }

      const response = await axios.get(`${API_BASE_URL}/invitations`, {
        headers: { 'x-auth-token': token },
        params
      });

      setSignupLinks(response.data.invitations || []);
      if (response.data.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching signup links:', error);
      setMessage('Failed to load signup links');
    } finally {
      setLoading(false);
    }
  };

  const copyInvitationLink = (signupCode) => {
    const invitationUrl = `${window.location.origin}/login?code=${signupCode}`;
    navigator.clipboard.writeText(invitationUrl);
    setMessage('✓ Signup link copied to clipboard');
    setTimeout(() => setMessage(''), 3000);
  };

  const openDetailsModal = async (invitation) => {
    setDetailsModalData(invitation);
    setShowDetailsModal(true);
  };

  const openQRModal = (invitation) => {
    const invitationUrl = `${window.location.origin}/login?code=${invitation.signupCode}`;
    setQRModalData({ ...invitation, url: invitationUrl });
    setShowQRModal(true);
  };

  const downloadQRCode = () => {
    if (!qrCodeRef.current) return;

    const svg = qrCodeRef.current.querySelector('svg');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.download = `qr-code-${qrModalData.signupCode}.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  const getStatusBadgeStyle = (status) => {
    const styles = {
      active: { background: '#10b981', color: 'white' },
      used: { background: '#6b7280', color: 'white' },
      expired: { background: '#ef4444', color: 'white' },
      revoked: { background: '#dc2626', color: 'white' },
      inactive: { background: '#9ca3af', color: 'white' },
      depleted: { background: '#f59e0b', color: 'white' }
    };
    return styles[status] || {};
  };

  const formatUsageStats = (invitation) => {
    if (!invitation.maxUses) return '∞';
    return `${invitation.currentUses || 0}/${invitation.maxUses}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
  };

  const handleStatusToggle = (status) => {
    const newStatus = filters.status.includes(status)
      ? filters.status.filter(s => s !== status)
      : [...filters.status, status];
    setFilters({ ...filters, status: newStatus });
  };

  const applyFilters = () => {
    setPagination({ ...pagination, page: 1 });
    fetchSignupLinks(true);
  };

  const clearFilters = () => {
    setFilters({
      serviceGroupId: '',
      status: ['active'],
      searchTerm: '',
      createdFrom: '',
      createdTo: ''
    });
    setPagination({ ...pagination, page: 1 });
  };

  const exportToCSV = () => {
    const headers = ['Code', 'Status', 'Service Group', 'Onboarding Flow', 'Inv. Group', 'Usage', 'Auto-Activate', 'Created', 'URL'];
    const rows = signupLinks.map(inv => [
      inv.signupCode,
      inv.status,
      inv.serviceGroupId?.name || '-',
      inv.onboardingFlowId?.name || '-',
      inv.invitationGroupId?.name || '-',
      formatUsageStats(inv),
      inv.autoActivate ? 'Yes' : 'No',
      formatDate(inv.createdAt),
      `${window.location.origin}/login?code=${inv.signupCode}`
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `signup-links-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="signup-links-widget">
      {/* Widget Card */}
      <div className="widget-card">
        <div className="widget-header">
          <h3>Signup Links</h3>
          <button
            className="view-all-btn"
            onClick={() => {
              setShowAllModal(true);
              fetchSignupLinks(true);
            }}
          >
            View All
          </button>
        </div>

        {message && (
          <div className={`message ${message.startsWith('✓') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}

        {loading && !showAllModal ? (
          <div className="loading">Loading...</div>
        ) : signupLinks.length === 0 ? (
          <div className="empty-state">
            <p>No active signup links found for your service groups.</p>
          </div>
        ) : (
          <>
            <div className="widget-table-container">
              <table className="widget-table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Service Group</th>
                    <th>Status</th>
                    <th>Usage</th>
                    <th>Expires</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signupLinks.map(link => (
                    <tr key={link._id} className={link.status === 'expired' || link.status === 'depleted' ? 'expired-row' : ''}>
                      <td className="code-cell">{link.signupCode}</td>
                      <td>{link.serviceGroupId?.name || '-'}</td>
                      <td>
                        <span
                          className="status-badge"
                          style={getStatusBadgeStyle(link.status)}
                        >
                          {link.status}
                        </span>
                      </td>
                      <td>{formatUsageStats(link)}</td>
                      <td>{formatDate(link.expiresAt)}</td>
                      <td className="actions-cell">
                        <button
                          className="icon-btn"
                          onClick={() => openDetailsModal(link)}
                          title="View Details"
                        >
                          👁
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => copyInvitationLink(link.signupCode)}
                          title="Copy Link"
                        >
                          📋
                        </button>
                        <button
                          className="icon-btn"
                          onClick={() => openQRModal(link)}
                          title="QR Code"
                        >
                          ⊞
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!showAllModal && (
              <div className="widget-footer">
                Showing {signupLinks.length} active link{signupLinks.length !== 1 ? 's' : ''}
              </div>
            )}
          </>
        )}
      </div>

      {/* Full Table Modal */}
      {showAllModal && (
        <div className="modal-overlay" onClick={() => setShowAllModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>All Signup Links</h2>
              <button className="close-btn" onClick={() => setShowAllModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* Filters */}
              <div className="filters-section">
                <div className="filter-row">
                  <div className="filter-group">
                    <label>Service Group</label>
                    <select
                      value={filters.serviceGroupId}
                      onChange={(e) => handleFilterChange('serviceGroupId', e.target.value)}
                    >
                      <option value="">All My Service Groups</option>
                      {serviceGroups.map(sg => (
                        <option key={sg._id} value={sg._id}>{sg.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>Search Code/Tag</label>
                    <input
                      type="text"
                      placeholder="Search..."
                      value={filters.searchTerm}
                      onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Status</label>
                    <div className="checkbox-group">
                      {['active', 'expired', 'depleted'].map(status => (
                        <label key={status} className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={filters.status.includes(status)}
                            onChange={() => handleStatusToggle(status)}
                          />
                          {status}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <label>Created From</label>
                    <input
                      type="date"
                      value={filters.createdFrom}
                      onChange={(e) => handleFilterChange('createdFrom', e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <label>Created To</label>
                    <input
                      type="date"
                      value={filters.createdTo}
                      onChange={(e) => handleFilterChange('createdTo', e.target.value)}
                    />
                  </div>

                  <div className="filter-actions">
                    <button className="apply-btn" onClick={applyFilters}>Apply Filters</button>
                    <button className="clear-btn" onClick={clearFilters}>Clear</button>
                    <button className="export-btn" onClick={exportToCSV}>Export CSV</button>
                  </div>
                </div>
              </div>

              {/* Full Table */}
              {loading ? (
                <div className="loading">Loading...</div>
              ) : (
                <>
                  <div className="full-table-container">
                    <table className="full-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Code</th>
                          <th>Status</th>
                          <th>Service Group</th>
                          <th>Onboarding Flow</th>
                          <th>Inv. Group</th>
                          <th>Usage</th>
                          <th>Auto-Activate</th>
                          <th>Created</th>
                          <th>Expires</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {signupLinks.map(link => (
                          <tr key={link._id} className={link.status === 'expired' || link.status === 'depleted' ? 'expired-row' : ''}>
                            <td>🔗 General Link</td>
                            <td className="code-cell">{link.signupCode}</td>
                            <td>
                              <span
                                className="status-badge"
                                style={getStatusBadgeStyle(link.status)}
                              >
                                {link.status}
                              </span>
                            </td>
                            <td>{link.serviceGroupId?.name || '-'}</td>
                            <td>{link.onboardingFlowId?.name || '-'}</td>
                            <td>{link.invitationGroupId?.name || '-'}</td>
                            <td>{formatUsageStats(link)}</td>
                            <td>{link.autoActivate ? '✓' : '✗'}</td>
                            <td>{formatDate(link.createdAt)}</td>
                            <td>{formatDate(link.expiresAt)}</td>
                            <td className="actions-cell">
                              <button
                                className="icon-btn"
                                onClick={() => openDetailsModal(link)}
                                title="View Details"
                              >
                                👁
                              </button>
                              <button
                                className="icon-btn"
                                onClick={() => copyInvitationLink(link.signupCode)}
                                title="Copy Link"
                              >
                                📋
                              </button>
                              <button
                                className="icon-btn"
                                onClick={() => openQRModal(link)}
                                title="QR Code"
                              >
                                ⊞
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="pagination">
                    <button
                      onClick={() => {
                        setPagination({ ...pagination, page: 1 });
                        setTimeout(() => fetchSignupLinks(true), 0);
                      }}
                      disabled={pagination.page === 1}
                    >
                      First
                    </button>
                    <button
                      onClick={() => {
                        setPagination({ ...pagination, page: pagination.page - 1 });
                        setTimeout(() => fetchSignupLinks(true), 0);
                      }}
                      disabled={pagination.page === 1}
                    >
                      Previous
                    </button>
                    <span>
                      Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                    </span>
                    <button
                      onClick={() => {
                        setPagination({ ...pagination, page: pagination.page + 1 });
                        setTimeout(() => fetchSignupLinks(true), 0);
                      }}
                      disabled={pagination.page >= pagination.pages}
                    >
                      Next
                    </button>
                    <button
                      onClick={() => {
                        setPagination({ ...pagination, page: pagination.pages });
                        setTimeout(() => fetchSignupLinks(true), 0);
                      }}
                      disabled={pagination.page >= pagination.pages}
                    >
                      Last
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && detailsModalData && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Signup Link Details</h2>
              <button className="close-btn" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="details-grid">
                <div className="detail-row">
                  <strong>Signup Code:</strong>
                  <span>{detailsModalData.signupCode}</span>
                </div>
                <div className="detail-row">
                  <strong>Status:</strong>
                  <span style={getStatusBadgeStyle(detailsModalData.status)}>
                    {detailsModalData.status}
                  </span>
                </div>
                <div className="detail-row">
                  <strong>Service Group:</strong>
                  <span>{detailsModalData.serviceGroupId?.name || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Onboarding Flow:</strong>
                  <span>{detailsModalData.onboardingFlowId?.name || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Invitation Group:</strong>
                  <span>{detailsModalData.invitationGroupId?.name || '-'}</span>
                </div>
                <div className="detail-row">
                  <strong>Usage:</strong>
                  <span>{formatUsageStats(detailsModalData)}</span>
                </div>
                <div className="detail-row">
                  <strong>Auto-Activate:</strong>
                  <span>{detailsModalData.autoActivate ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-row">
                  <strong>Created:</strong>
                  <span>{formatDate(detailsModalData.createdAt)}</span>
                </div>
                {detailsModalData.expiresAt && (
                  <div className="detail-row">
                    <strong>Expires:</strong>
                    <span>{formatDate(detailsModalData.expiresAt)}</span>
                  </div>
                )}
                {detailsModalData.generalLinkSettings?.tag && (
                  <div className="detail-row">
                    <strong>Tag:</strong>
                    <span>{detailsModalData.generalLinkSettings.tag}</span>
                  </div>
                )}
                {detailsModalData.generalLinkSettings?.description && (
                  <div className="detail-row">
                    <strong>Description:</strong>
                    <span>{detailsModalData.generalLinkSettings.description}</span>
                  </div>
                )}
                <div className="detail-row">
                  <strong>Signup URL:</strong>
                  <span className="url-text">
                    {window.location.origin}/login?code={detailsModalData.signupCode}
                  </span>
                </div>
              </div>

              {detailsModalData.usageLog && detailsModalData.usageLog.length > 0 && (
                <div className="usage-log">
                  <h3>Usage History</h3>
                  <table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailsModalData.usageLog.map((log, idx) => (
                        <tr key={idx}>
                          <td>{formatDate(log.usedAt)}</td>
                          <td>{log.userId?.email || log.userId || 'Unknown'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && qrModalData && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>QR Code - {qrModalData.signupCode}</h2>
              <button className="close-btn" onClick={() => setShowQRModal(false)}>×</button>
            </div>
            <div className="modal-body qr-modal-body">
              <div ref={qrCodeRef} className="qr-code-container">
                <QRCodeSVG
                  value={qrModalData.url}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
              </div>
              <p className="qr-code-text">{qrModalData.signupCode}</p>
              <p className="qr-url">{qrModalData.url}</p>
              <div className="qr-actions">
                <button onClick={downloadQRCode}>Download QR Code</button>
                <button onClick={() => window.print()}>Print</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SignupLinksWidget;
